/**
 * API service for text asset recommendations (headlines, descriptions).
 */
import apiClient from './client';

export interface AssetRecommendationRequest {
  member_uuid: string;
  campaign_id: string;
  campaign_type: string;
  entity_id: string;
  entity_type: 'asset_group' | 'ad_group' | 'campaign';
  asset_type: 'headline' | 'description' | 'long_headline' | 'sitelink' | 'callout' | 'structured_snippet';
  max_recommendations?: number;
  analyze_images?: boolean;
}

export interface RecommendedAsset {
  text: string;
  confidence: number;
  relevance_score?: number;
  character_count: number;
  reasoning?: string;
  final_url?: string; // For sitelinks - destination URL
}

export interface AssetRecommendationResponse {
  member_uuid: string;
  campaign_id: string;
  campaign_type: string;
  entity_id: string;
  entity_type: string;
  asset_type: string;
  recommendations: RecommendedAsset[];
  landing_page_url?: string;
  context_summary?: string;
  current_assets_count: number;
  images_analyzed: number;
  target_count: number;
  recommendations_needed: number;
  message?: string;
}

export interface SaveAssetsRequest {
  member_uuid: string;
  campaign_id: string;
  campaign_type: string;
  entity_id: string;
  entity_type: 'asset_group' | 'ad_group' | 'campaign';
  customer_id?: string;
  selected_assets: Array<{
    text: string;
    asset_type: string;
    confidence?: number;
    final_url?: string;  // Required for sitelinks
    description1?: string;  // Optional for sitelinks
    description2?: string;  // Optional for sitelinks
  }>;
  trigger_data_sync?: boolean;
}

export interface SaveAssetsResponse {
  success: boolean;
  message: string;
  successful_assets: Array<{
    text: string;
    asset_type: string;
    asset_id?: string;
    resource_name?: string;
  }>;
  failed_assets: Array<{
    text: string;
    asset_type: string;
    error: string;
  }>;
  data_sync_triggered: boolean;
}

/**
 * Generate AI-powered text asset recommendations.
 * Uses a longer timeout (2 minutes) since this involves:
 * - Database queries for landing page content
 * - Downloading images from Google Cloud Storage
 * - Calling Gemini API for AI-powered recommendations
 */
export async function generateAssetRecommendations(
  request: AssetRecommendationRequest
): Promise<AssetRecommendationResponse> {
  const response = await apiClient.post<AssetRecommendationResponse>(
    '/assets/recommendations',
    {
      ...request,
      max_recommendations: request.max_recommendations ?? 10,
      analyze_images: request.analyze_images ?? true,
    },
    {
      timeout: 120000, // 2 minutes for Gemini API + GCS image downloads
    }
  );
  return response.data;
}

/**
 * Save selected asset recommendations to Google Ads.
 * Uses a longer timeout (2 minutes) since Google Ads API calls can take time.
 */
export async function saveSelectedAssets(
  request: SaveAssetsRequest
): Promise<SaveAssetsResponse> {
  const response = await apiClient.post<SaveAssetsResponse>(
    '/assets/save',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 120000, // 2 minutes for Google Ads API calls
    }
  );
  return response.data;
}

// Campaign-level asset management types and functions

export interface CampaignAsset {
  asset_id: string;
  resource_name: string;
  text: string;
  asset_type: 'headline' | 'description' | 'long_headline' | 'sitelink' | 'callout' | 'structured_snippet';
  field_type: string;
  status: 'ENABLED' | 'PAUSED' | 'REMOVED' | 'UNDEFINED';
  final_url?: string; // For sitelinks
}

export interface GetCampaignAssetsRequest {
  member_uuid: string;
  campaign_id?: string;  // Optional for backward compatibility
  ad_group_id?: string;  // Recommended for Search/Display campaigns (API accessible)
  customer_id?: string;
  asset_type?: 'headline' | 'description' | 'long_headline' | 'sitelink' | 'callout' | 'structured_snippet';
}

export interface GetCampaignAssetsResponse {
  success: boolean;
  campaign_id: string;
  assets: CampaignAsset[];
  count: number;
  error?: string;
}

export interface UpdateAssetStatusRequest {
  member_uuid: string;
  campaign_id?: string;  // For campaign-level assets
  ad_group_id?: string;  // For Search/Display ad group assets
  asset_group_id?: string;  // For Performance Max asset group assets
  customer_id?: string;
  asset_ids: string[];
  action: 'enable' | 'pause' | 'remove';
  trigger_data_sync?: boolean;
}

export interface UpdateAssetStatusResponse {
  success: boolean;
  message: string;
  successful_count: number;
  failed_count: number;
  skipped_count: number;
  successful_assets: Array<{
    asset_id: string;
    resource_name: string;
  }>;
  failed_assets: Array<{
    asset_id: string;
    error: string;
  }>;
  skipped_assets: Array<{
    asset_id: string;
    reason: string;
  }>;
  data_sync_triggered: boolean;
}

/**
 * Get campaign-level assets from Google Ads (for Search/Display campaigns).
 * These are assets linked to the campaign level that apply to all RSAs.
 */
export async function getCampaignAssets(
  request: GetCampaignAssetsRequest
): Promise<GetCampaignAssetsResponse> {
  const response = await apiClient.post<GetCampaignAssetsResponse>(
    '/assets/campaign/assets',
    request
  );
  return response.data;
}

/**
 * Update the status of campaign-level assets (batch operation).
 * Similar to keyword batch updates - supports Enable, Pause, and Remove actions.
 */
export async function updateAssetStatus(
  request: UpdateAssetStatusRequest
): Promise<UpdateAssetStatusResponse> {
  const response = await apiClient.post<UpdateAssetStatusResponse>(
    '/assets/status/update',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 60000, // 1 minute for batch operations
    }
  );
  return response.data;
}


// =============================================================================
// Image Asset Types and Functions
// =============================================================================

/**
 * Image types for Google Ads Performance Max asset groups.
 * Based on: https://developers.google.com/google-ads/api/performance-max/asset-requirements
 */
export type ImageAssetType =
  | 'marketing_image'           // Landscape 1.91:1 (1200x628 recommended)
  | 'square_marketing_image'    // Square 1:1 (1200x1200 recommended)
  | 'portrait_marketing_image'  // Portrait 4:5 (960x1200 recommended)
  | 'logo'                       // Square logo 1:1 (1200x1200 recommended)
  | 'landscape_logo';           // Landscape logo 4:1 (1200x300 recommended)

export interface SaveImageAssetsRequest {
  member_uuid: string;
  campaign_id?: string;  // For usage tracking
  asset_group_id: string;
  customer_id?: string;
  image_ids: string[];
  image_type: ImageAssetType;
  trigger_data_sync?: boolean;
}

/**
 * Error details for Google Ads account restrictions.
 */
export interface GoogleAdsErrorDetails {
  title: string;
  description: string;
  requirements?: string[];
  note?: string;
  help_url: string;
}

export interface SaveImageAssetsResponse {
  success: boolean;
  message: string;
  successful_images: Array<{
    image_id: string;
    image_name: string;
    asset_id?: string;
    resource_name?: string;
    image_type?: string;
  }>;
  failed_images: Array<{
    image_id?: string;
    image_name?: string;
    error: string;
  }>;
  data_sync_triggered: boolean;
  /** Specific error code for handling (e.g., 'ACCOUNT_NOT_ELIGIBLE_FOR_IMAGE_ASSETS') */
  error_code?: string;
  /** Additional error details including help URLs */
  error_details?: GoogleAdsErrorDetails;
}

/**
 * Upload images from Media Library to Google Ads as image assets.
 *
 * This uploads the selected Media Library images to Google Ads and links
 * them to the specified asset group with the appropriate field type.
 *
 * Uses a longer timeout (2 minutes) since this involves:
 * - Downloading images from GCS
 * - Uploading to Google Ads API
 * - Creating asset-to-asset-group links
 */
export async function saveImageAssets(
  request: SaveImageAssetsRequest
): Promise<SaveImageAssetsResponse> {
  const response = await apiClient.post<SaveImageAssetsResponse>(
    '/assets/images/save',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 120000, // 2 minutes for Google Ads API + GCS downloads
    }
  );
  return response.data;
}

export interface ImageProxyRequest {
  member_uuid: string;
  asset_id: string;
  image_url: string;
  asset_type?: ImageAssetType;
}

export interface ImageProxyResponse {
  url: string;
  cached: boolean;
  fallback?: boolean;
  error?: string;
}

/**
 * Get a cached/proxied URL for a Google Ads image asset.
 *
 * Google Ads returns temporary URLs that expire. This endpoint:
 * 1. Checks if the image is already cached in GCS
 * 2. If not, downloads and caches it
 * 3. Returns a signed URL that works reliably
 */
export async function getGoogleAdsImageUrl(
  request: ImageProxyRequest
): Promise<ImageProxyResponse> {
  const response = await apiClient.post<ImageProxyResponse>(
    '/assets/images/proxy',
    null,
    {
      params: {
        member_uuid: request.member_uuid,
        asset_id: request.asset_id,
        image_url: request.image_url,
        asset_type: request.asset_type || 'marketing_image',
      },
      timeout: 30000, // 30 seconds for download + cache
    }
  );
  return response.data;
}

// =============================================================================
// Image Management Types & Functions (Media Library <-> Google Ads sync)
// =============================================================================

export interface LinkedImageInfo {
  image_id: string;
  usage_id: string;
  filename: string;
  gcs_path: string;
  thumbnail_url: string | null;
  width: number | null;
  height: number | null;
  google_ads_asset_id: string | null;
  google_ads_resource_name: string | null;
  image_field_type: ImageAssetType | null;
  linked_at: string | null;
}

export interface GetLinkedImagesRequest {
  member_uuid: string;
  asset_group_id: string;
  customer_id?: string;
}

export interface GetLinkedImagesResponse {
  success: boolean;
  asset_group_id: string;
  images: LinkedImageInfo[];
  count: number;
  error?: string;
}

export interface RemoveImageFromAssetGroupRequest {
  member_uuid: string;
  asset_group_id: string;
  google_ads_asset_id: string;
  customer_id?: string;
  trigger_data_sync?: boolean;
}

export interface RemoveImageFromAssetGroupResponse {
  success: boolean;
  message: string;
  google_ads_asset_id: string;
  data_sync_triggered: boolean;
  error?: string;
}

export interface ChangeImageAssetTypeRequest {
  member_uuid: string;
  entity_id: string;
  entity_type: 'asset_group' | 'ad_group' | 'campaign';
  google_ads_asset_id: string;
  new_image_type: ImageAssetType;
  image_id: string;  // Media Library image ID for re-cropping
  customer_id?: string;
  campaign_id?: string;  // Required for ad_group entity type
  trigger_data_sync?: boolean;
}

export interface ChangeImageAssetTypeResponse {
  success: boolean;
  message: string;
  google_ads_asset_id: string;
  old_image_type: ImageAssetType | null;
  new_image_type: ImageAssetType;
  data_sync_triggered: boolean;
  error?: string;
}

/**
 * Get all Media Library images linked to an asset group.
 *
 * Returns images uploaded via Flouri with their thumbnails, metadata,
 * and Google Ads asset information.
 */
export async function getLinkedImagesByAssetGroup(
  request: GetLinkedImagesRequest
): Promise<GetLinkedImagesResponse> {
  const response = await apiClient.post<GetLinkedImagesResponse>(
    '/assets/images/by-asset-group',
    request
  );
  return response.data;
}

/**
 * Remove an image from a Google Ads asset group.
 *
 * This removes the asset link from Google Ads and marks the MediaImageUsage
 * record as unlinked. The image remains in the Media Library.
 */
export async function removeImageFromAssetGroup(
  request: RemoveImageFromAssetGroupRequest
): Promise<RemoveImageFromAssetGroupResponse> {
  const response = await apiClient.post<RemoveImageFromAssetGroupResponse>(
    '/assets/images/remove-from-asset-group',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 60000, // 1 minute for Google Ads API
    }
  );
  return response.data;
}

/**
 * Change the field type (aspect ratio category) of an image in Google Ads.
 *
 * This removes the old asset link and creates a new one with the new field type.
 * The underlying Google Ads asset remains the same.
 */
export async function changeImageAssetType(
  request: ChangeImageAssetTypeRequest
): Promise<ChangeImageAssetTypeResponse> {
  const response = await apiClient.post<ChangeImageAssetTypeResponse>(
    '/assets/images/change-asset-type',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 90000, // 1.5 minutes for remove + create operations
    }
  );
  return response.data;
}


// =============================================================================
// Generic Multi-Entity Image Asset Functions
// =============================================================================

/**
 * Entity types supported for image uploads.
 * - asset_group: Performance Max asset groups (all image types)
 * - campaign: Campaign-level image extensions (landscape + square only)
 * - ad_group: Ad-group-level image extensions (landscape + square only)
 * - ad: Ad-level images (for RDAs - Responsive Display Ads)
 */
export type ImageEntityType = 'asset_group' | 'campaign' | 'ad_group' | 'ad';

export interface SaveImageAssetsGenericRequest {
  member_uuid: string;
  entity_type: ImageEntityType;
  entity_id: string;
  customer_id?: string;
  image_ids: string[];
  image_type: ImageAssetType;
  campaign_id?: string;  // Required for ad_group entity type
  trigger_data_sync?: boolean;
}

export interface GetLinkedImagesByEntityRequest {
  member_uuid: string;
  entity_type: ImageEntityType;
  entity_id: string;
  customer_id?: string;
}

export interface GetLinkedImagesByEntityResponse {
  success: boolean;
  entity_type: ImageEntityType;
  entity_id: string;
  images: LinkedImageInfo[];
  count: number;
  error?: string;
}

export interface RemoveImageFromEntityRequest {
  member_uuid: string;
  entity_type: ImageEntityType;
  entity_id: string;
  google_ads_asset_id: string;
  customer_id?: string;
  trigger_data_sync?: boolean;
}

export interface RemoveImageFromEntityResponse {
  success: boolean;
  message: string;
  entity_type: ImageEntityType;
  entity_id: string;
  google_ads_asset_id: string;
  data_sync_triggered: boolean;
  error?: string;
}

/**
 * Upload images from Media Library to Google Ads for any entity type.
 *
 * This generic function supports:
 * - asset_group: Performance Max asset groups (all image types)
 * - campaign: Campaign-level image extensions (landscape + square only)
 * - ad_group: Ad-group-level image extensions (landscape + square only)
 *
 * Uses a longer timeout (2 minutes) since this involves:
 * - Downloading images from GCS
 * - Uploading to Google Ads API
 * - Creating entity-to-asset links
 */
export async function saveImageAssetsGeneric(
  request: SaveImageAssetsGenericRequest
): Promise<SaveImageAssetsResponse> {
  const response = await apiClient.post<SaveImageAssetsResponse>(
    '/assets/images/save-generic',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 120000, // 2 minutes for Google Ads API + GCS downloads
    }
  );
  return response.data;
}

/**
 * Get all Media Library images linked to any entity type.
 *
 * Returns images with their thumbnails, dimensions, and Google Ads asset info.
 */
export async function getLinkedImagesByEntity(
  request: GetLinkedImagesByEntityRequest
): Promise<GetLinkedImagesByEntityResponse> {
  const response = await apiClient.post<GetLinkedImagesByEntityResponse>(
    '/assets/images/by-entity',
    request
  );
  return response.data;
}

/**
 * Remove an image from any entity type (campaign, ad_group, or asset_group).
 *
 * This removes the asset link from Google Ads and marks the MediaImageUsage
 * record as unlinked. The image remains in the Media Library.
 */
export async function removeImageFromEntity(
  request: RemoveImageFromEntityRequest
): Promise<RemoveImageFromEntityResponse> {
  const response = await apiClient.post<RemoveImageFromEntityResponse>(
    '/assets/images/remove-from-entity',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 60000, // 1 minute for Google Ads API
    }
  );
  return response.data;
}


// =============================================================================
// RSA (Responsive Search Ad) Management Types and Functions
// =============================================================================

export interface RSATextAsset {
  text: string;
  pinned_field?: string | null;  // HEADLINE_1, HEADLINE_2, HEADLINE_3, DESCRIPTION_1, DESCRIPTION_2
}

export interface GetRSARequest {
  member_uuid: string;
  ad_group_id: string;
  ad_id: string;
  customer_id?: string;
}

export interface GetRSAResponse {
  success: boolean;
  ad_id?: string;
  resource_name?: string;
  ad_type?: string;
  headlines: RSATextAsset[];
  descriptions: RSATextAsset[];
  final_urls: string[];
  path1?: string;
  path2?: string;
  status?: string;
  ad_group_id?: string;
  ad_group_name?: string;
  error?: string;
}

export interface UpdateRSARequest {
  member_uuid: string;
  ad_group_id: string;
  ad_id: string;
  customer_id?: string;
  headlines?: RSATextAsset[];
  descriptions?: RSATextAsset[];
  final_urls?: string[];
  path1?: string;
  path2?: string;
  trigger_data_sync?: boolean;
}

export interface UpdateRSAResponse {
  success: boolean;
  message: string;
  ad_id?: string;
  resource_name?: string;
  headlines_updated: number;
  descriptions_updated: number;
  data_sync_triggered: boolean;
  error?: string;
}

/**
 * Get details of a Responsive Search Ad including all headlines and descriptions.
 *
 * Returns the full RSA content with headlines (3-15), descriptions (2-4),
 * pinning positions, final URLs, and status.
 */
export async function getRSA(
  request: GetRSARequest
): Promise<GetRSAResponse> {
  const response = await apiClient.post<GetRSAResponse>(
    '/assets/rsa/get',
    request
  );
  return response.data;
}

/**
 * Update headlines and/or descriptions of a Responsive Search Ad.
 *
 * Google Ads allows editing RSA content after creation. Requirements:
 * - Headlines: 3-15 required (when updating), max 30 chars each
 * - Descriptions: 2-4 required (when updating), max 90 chars each
 * - At least one of headlines or descriptions must be provided
 *
 * Uses a longer timeout since Google Ads API updates can take time.
 */
export async function updateRSA(
  request: UpdateRSARequest
): Promise<UpdateRSAResponse> {
  const response = await apiClient.post<UpdateRSAResponse>(
    '/assets/rsa/update',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 90000, // 1.5 minutes for Google Ads API
    }
  );
  return response.data;
}

// =============================================================================
// RDA (Responsive Display Ad) Types and Functions
// =============================================================================

/**
 * RDA image asset - represents an image embedded in a Responsive Display Ad
 */
export interface RDAImageAsset {
  asset_id: string;
  url?: string;
  width?: number;
  height?: number;
}

/**
 * Request to get RDA details
 */
export interface GetRDARequest {
  member_uuid: string;
  ad_group_id: string;
  ad_id: string;
  customer_id?: string;
}

/**
 * Response containing RDA details
 */
export interface GetRDAResponse {
  success: boolean;
  ad_id?: string;
  resource_name?: string;
  ad_type?: string;
  // Text assets
  headlines: string[];
  long_headline?: string;
  descriptions: string[];
  business_name?: string;
  final_urls: string[];
  // Image assets (embedded in the RDA)
  marketing_images: RDAImageAsset[];
  square_marketing_images: RDAImageAsset[];
  logo_images: RDAImageAsset[];
  landscape_logo_images: RDAImageAsset[];
  // Status info
  status?: string;
  ad_group_id?: string;
  ad_group_name?: string;
  error?: string;
}

/**
 * Request to update RDA text and image assets
 */
export interface UpdateRDARequest {
  member_uuid: string;
  campaign_id?: string;  // For usage tracking
  ad_group_id: string;
  ad_id: string;
  customer_id?: string;
  // Text assets
  headlines?: string[];
  long_headline?: string;
  descriptions?: string[];
  business_name?: string;
  // Existing image asset IDs to KEEP (Google Ads asset IDs)
  marketing_images?: string[];
  square_marketing_images?: string[];
  logo_images?: string[];
  landscape_logo_images?: string[];
  // NEW images from Media Library (will be uploaded to Google Ads)
  new_marketing_image_ids?: string[];
  new_square_image_ids?: string[];
  new_logo_image_ids?: string[];
  new_landscape_logo_image_ids?: string[];
  // Final URLs
  final_urls?: string[];
  trigger_data_sync?: boolean;
}

/**
 * Response from updating RDA
 */
export interface UpdateRDAResponse {
  success: boolean;
  message: string;
  ad_id?: string;
  resource_name?: string;
  headlines_count: number;
  descriptions_count: number;
  marketing_images_count: number;
  square_images_count: number;
  new_images_uploaded: number;
  data_sync_triggered: boolean;
  error?: string;
}

/**
 * Get details of a Responsive Display Ad including all text and image assets.
 *
 * Returns the full RDA content with headlines, long_headline, descriptions,
 * business_name, final URLs, and all image assets.
 */
export async function getRDA(
  request: GetRDARequest
): Promise<GetRDAResponse> {
  const response = await apiClient.post<GetRDAResponse>(
    '/assets/rda/get',
    request
  );
  return response.data;
}

/**
 * Update text and/or image assets of a Responsive Display Ad.
 *
 * Unlike RSAs which only have text, RDAs have both text AND images embedded in the ad.
 * This function handles:
 * - Keeping existing image assets (by asset_id)
 * - Uploading new images from Media Library
 * - Updating all text assets
 *
 * Note: Google Ads requires creating a new RDA and removing the old one for updates
 * since RDAs are immutable. This is handled automatically by the backend.
 *
 * Uses a longer timeout since image uploads can take time.
 */
export async function updateRDA(
  request: UpdateRDARequest
): Promise<UpdateRDAResponse> {
  const response = await apiClient.post<UpdateRDAResponse>(
    '/assets/rda/update',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 180000, // 3 minutes for image uploads + Google Ads API
    }
  );
  return response.data;
}

// =============================================================================
// Demand Gen Ad Types and Functions
// =============================================================================

/**
 * Demand Gen ad types
 */
export type DemandGenAdType = 'MULTI_ASSET' | 'VIDEO' | 'CAROUSEL';

/**
 * Carousel card data for Demand Gen Carousel ads
 */
export interface DemandGenCarouselCard {
  headline: string;
  imageId: string;
  imageUrl?: string;  // For display
  finalUrl: string;
}

/**
 * Request to get Demand Gen ad details
 */
export interface GetDemandGenAdRequest {
  member_uuid: string;
  ad_group_id: string;
  ad_id: string;
  customer_id?: string;
}

/**
 * Response containing Demand Gen ad details
 */
export interface GetDemandGenAdResponse {
  success: boolean;
  ad_id?: string;
  ad_name?: string;  // Ad name (visible in Google Ads console)
  resource_name?: string;
  ad_type?: DemandGenAdType;
  // Common fields
  business_name?: string;
  call_to_action?: string;
  final_urls: string[];
  status?: string;
  ad_group_id?: string;
  ad_group_name?: string;
  // Multi-Asset fields
  headlines: string[];
  descriptions: string[];
  marketing_images: RDAImageAsset[];  // Reuse RDA image type (landscape 1.91:1)
  square_marketing_images: RDAImageAsset[];  // Square 1:1
  portrait_marketing_images: RDAImageAsset[];  // Portrait 4:5
  logo_images: RDAImageAsset[];  // Logo 1:1
  // Video fields
  long_headlines: string[];
  video_ids: string[];  // YouTube video IDs
  // Carousel fields
  carousel_cards: Array<{
    headline: string;
    image_id?: string;  // Media Library image ID (for uploads)
    asset_id?: string;  // Google Ads asset ID (for existing cards)
    final_url?: string;
  }>;
  error?: string;
}

/**
 * Request to update Demand Gen ad
 */
export interface UpdateDemandGenAdRequest {
  member_uuid: string;
  campaign_id?: string;  // For usage tracking
  ad_group_id: string;
  ad_id: string;
  customer_id?: string;
  ad_type?: DemandGenAdType;  // Optional - backend will detect from existing ad
  // Common fields
  ad_name?: string;  // Ad name (visible in Google Ads console)
  business_name?: string;
  call_to_action?: string;
  final_urls?: string[];  // Final URLs (landing page URLs) - backend expects array
  // Multi-Asset and Video text
  headlines?: string[];
  descriptions?: string[];
  // Video-specific
  long_headlines?: string[];
  video_ids?: string[];
  // Existing image asset IDs to KEEP
  marketing_images?: string[];
  square_marketing_images?: string[];
  portrait_marketing_images?: string[];
  logo_images?: string[];
  // NEW images from Media Library
  new_marketing_image_ids?: string[];
  new_square_image_ids?: string[];
  new_portrait_image_ids?: string[];
  new_logo_image_ids?: string[];
  // Carousel cards
  carousel_cards?: Array<{
    headline: string;
    image_id: string;  // Media Library image ID
    final_url: string;
  }>;
  trigger_data_sync?: boolean;
}

/**
 * Response from updating Demand Gen ad
 */
export interface UpdateDemandGenAdResponse {
  success: boolean;
  message: string;
  ad_id?: string;
  resource_name?: string;
  ad_type?: string;  // MULTI_ASSET, VIDEO, or CAROUSEL
  headlines_count: number;
  descriptions_count: number;
  marketing_images_count: number;
  square_images_count: number;
  portrait_images_count: number;
  logo_images_count: number;
  carousel_cards_count: number;
  new_images_uploaded: number;
  data_sync_triggered: boolean;
  error?: string;
}

/**
 * Get details of a Demand Gen ad.
 *
 * Supports Multi-Asset, Video, and Carousel ad types.
 * Returns full content including text assets, images, and videos.
 */
export async function getDemandGenAd(
  request: GetDemandGenAdRequest
): Promise<GetDemandGenAdResponse> {
  const response = await apiClient.post<GetDemandGenAdResponse>(
    '/assets/demand-gen/get',
    request
  );
  return response.data;
}

/**
 * Update a Demand Gen ad's text and image assets.
 *
 * Handles:
 * - Keeping existing images (by asset_id)
 * - Uploading new images from Media Library
 * - Updating text assets (headlines, descriptions)
 * - Updating carousel cards for Carousel ads
 *
 * Note: Like RDAs, Google Ads requires creating a new ad and removing the old one
 * for updates since Demand Gen ads are immutable. This is handled by the backend.
 */
export async function updateDemandGenAd(
  request: UpdateDemandGenAdRequest
): Promise<UpdateDemandGenAdResponse> {
  const response = await apiClient.post<UpdateDemandGenAdResponse>(
    '/assets/demand-gen/update',
    {
      ...request,
      trigger_data_sync: request.trigger_data_sync ?? true,
    },
    {
      timeout: 180000, // 3 minutes for image uploads + Google Ads API
    }
  );
  return response.data;
}
